import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RegistrationForm({ variant = "dark" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", business: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.name || !form.email || !form.phone || !form.business) {
      toast.error("Please fill out every field.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/register`, form);
      setDone(true);
      toast.success("Your seat is reserved.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={TEST_IDS.regSuccess}
        className="rounded-2xl p-8 md:p-10 text-center"
        style={{
          background:
            "linear-gradient(135deg, rgba(0,240,255,0.08) 0%, rgba(212,175,55,0.08) 100%)",
          border: "1px solid rgba(0,240,255,0.25)",
        }}
      >
        <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#D4AF37] text-[#030712] mb-4">
          <CheckCircle2 size={28} />
        </div>
        <h3 className="font-display text-3xl md:text-4xl font-black mb-3">
          You're on the list.
        </h3>
        <p className="text-white/80 max-w-md mx-auto leading-relaxed">
          We've saved your seat for <span className="text-[#D4AF37] font-semibold">August 1st, 2026</span> at the Shaquille O'Neal Movie Theater. Watch your inbox — Lovefavor will personally send you the time, parking details, and what to bring.
        </p>
      </div>
    );
  }

  return (
    <form
      data-testid={TEST_IDS.regForm}
      onSubmit={onSubmit}
      className="rounded-2xl p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4"
      style={{
        background:
          variant === "dark"
            ? "rgba(255,255,255,0.04)"
            : "rgba(3,7,18,0.04)",
        border:
          variant === "dark"
            ? "1px solid rgba(255,255,255,0.12)"
            : "1px solid rgba(3,7,18,0.12)",
      }}
    >
      <div className="flex flex-col gap-2 md:col-span-1">
        <label className="text-xs uppercase tracking-[0.2em] text-cyan-300/80">
          Your Name
        </label>
        <input
          data-testid={TEST_IDS.regNameInput}
          type="text"
          value={form.name}
          onChange={update("name")}
          placeholder="Jane Doe"
          className="input-dark rounded-lg"
          required
        />
      </div>
      <div className="flex flex-col gap-2 md:col-span-1">
        <label className="text-xs uppercase tracking-[0.2em] text-cyan-300/80">
          Email
        </label>
        <input
          data-testid={TEST_IDS.regEmailInput}
          type="email"
          value={form.email}
          onChange={update("email")}
          placeholder="you@business.com"
          className="input-dark rounded-lg"
          required
        />
      </div>
      <div className="flex flex-col gap-2 md:col-span-1">
        <label className="text-xs uppercase tracking-[0.2em] text-cyan-300/80">
          Phone
        </label>
        <input
          data-testid={TEST_IDS.regPhoneInput}
          type="tel"
          value={form.phone}
          onChange={update("phone")}
          placeholder="(555) 123-4567"
          className="input-dark rounded-lg"
          required
        />
      </div>
      <div className="flex flex-col gap-2 md:col-span-1">
        <label className="text-xs uppercase tracking-[0.2em] text-cyan-300/80">
          Business
        </label>
        <input
          data-testid={TEST_IDS.regBusinessInput}
          type="text"
          value={form.business}
          onChange={update("business")}
          placeholder="What do you do?"
          className="input-dark rounded-lg"
          required
        />
      </div>
      <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-2">
        <p className="text-xs text-white/55 leading-relaxed max-w-sm">
          By registering you'll receive event details from Favor Studio. No spam. Unsubscribe anytime.
        </p>
        <button
          data-testid={TEST_IDS.regSubmit}
          type="submit"
          disabled={loading}
          className="btn-gold rounded-md"
        >
          {loading ? "Reserving…" : "Reserve My Free Seat"}
          {!loading && <ArrowRight size={18} />}
        </button>
      </div>
    </form>
  );
}
